import type {
  CellTemplateProp,
  ColumnData,
  ColumnRegular,
  HyperFunc,
  VNode,
} from '@revolist/react-datagrid';

import { RevoGridTableExtras } from './types';

/** Not real row data — the column is readonly and never written to. */
export const REVO_GRID_SELECTION_PROP = '__selected';

export const REVO_GRID_CHECKBOX_CLASS = 'sm-checkbox';
export const REVO_GRID_CHECKBOX_INPUT_CLASS = 'sm-checkbox-input';
export const REVO_GRID_CHECKBOX_CHECKED_CLASS = 'checked';

const SELECTION_COLUMN_SIZE = 68;

const CHECKBOX_PATH =
  'M17.9 2.318A5 5 0 0 1 22.895 7.1l.005.217v10a5 5 0 0 1-4.783 4.995l-.217.005h-10a5 5 0 0 1-4.995-4.783l-.005-.217v-10a5 5 0 0 1 4.783-4.996l.217-.004h10Zm-.5 1.5h-9a4 4 0 0 0-4 4v9a4 4 0 0 0 4 4h9a4 4 0 0 0 4-4v-9a4 4 0 0 0-4-4Z';

const CHECKBOX_CHECKED_PATH =
  'M17 2a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm-1.625 7.255-4.13 4.13-1.75-1.75a.881.881 0 0 0-1.24 0c-.34.34-.34.89 0 1.24l2.38 2.37c.17.17.39.25.61.25.23 0 .45-.08.62-.25l4.75-4.75c.34-.34.34-.89 0-1.24a.881.881 0 0 0-1.24 0Z';

const CHECKBOX_INDETERMINATE_PATH =
  'M17,2 C19.7614,2 22,4.23858 22,7 L22,7 L22,17 C22,19.7614 19.7614,22 17,22 L17,22 L7,22 C4.23858,22 2,19.7614 2,17 L2,17 L2,7 C2,4.23858 4.23858,2 7,2 L7,2 Z M15,11 L9,11 C8.44772,11 8,11.4477 8,12 C8,12.5523 8.44772,13 9,13 L15,13 C15.5523,13 16,12.5523 16,12 C16,11.4477 15.5523,11 15,11 Z';

type CheckboxProps = {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  dataSm: string;
  onToggle: () => void;
};

const getCheckboxPath = ({ checked, indeterminate }: CheckboxProps): string => {
  if (indeterminate) {
    return CHECKBOX_INDETERMINATE_PATH;
  }
  return checked ? CHECKBOX_CHECKED_PATH : CHECKBOX_PATH;
};

const renderCheckbox = (h: HyperFunc<VNode>, props: CheckboxProps) => {
  const { checked, indeterminate, label, dataSm, onToggle } = props;

  return h(
    'span',
    { style: { display: 'flex', alignItems: 'center', height: '100%' } },
    h(
      'span',
      {
        class: `${REVO_GRID_CHECKBOX_CLASS}${
          checked || indeterminate ? ` ${REVO_GRID_CHECKBOX_CHECKED_CLASS}` : ''
        }`,
      },
      [
        h('input', {
          type: 'checkbox',
          class: REVO_GRID_CHECKBOX_INPUT_CLASS,
          'aria-label': label,
          'data-sm': dataSm,
          checked,
          indeterminate: !!indeterminate,
          onMouseDown: (event: MouseEvent) => event.stopPropagation(),
          onClick: (event: MouseEvent) => event.stopPropagation(),
          onKeyDown: (event: KeyboardEvent) => event.stopPropagation(),
          onChange: () => onToggle(),
        }),
        h(
          'svg',
          { viewBox: '0 0 24 24', 'aria-hidden': 'true', focusable: 'false' },
          h('path', { fill: 'currentColor', d: getCheckboxPath(props) })
        ),
      ]
    )
  );
};

export const SELECTION_COLUMN: ColumnRegular = {
  prop: REVO_GRID_SELECTION_PROP,
  name: '',
  size: SELECTION_COLUMN_SIZE,
  // keeps the column out of inline editing and out of range-fill / paste writes
  readonly: true,
  sortable: false,
  filter: false,

  cellProperties: () => ({ style: { backgroundColor: 'transparent' } }),
  columnTemplate: (h: HyperFunc<VNode>, _column, extra?: RevoGridTableExtras) => {
    const selection = extra?.selection;

    return renderCheckbox(h, {
      checked: !!selection?.allSelected,
      indeterminate: !!selection?.someSelected && !selection?.allSelected,
      label: 'Select all rows',
      dataSm: 'revo-grid-select-all',
      onToggle: () => selection?.onToggleAll(),
    });
  },
  cellTemplate: (h: HyperFunc<VNode>, props: CellTemplateProp, extra?: RevoGridTableExtras) => {
    const selection = extra?.selection;
    const id = selection?.getRowId(props.model) ?? '';

    return renderCheckbox(h, {
      checked: !!selection?.selectedIds.has(id),
      label: `Select row ${id}`,
      dataSm: `revo-grid-select-row-${id}`,
      onToggle: () => selection?.onToggleRow(id),
    });
  },
};

/** Prepends the selection column, leaving any column grouping untouched. */
export const withSelectionColumn = (columns: ColumnData): ColumnData => [
  SELECTION_COLUMN,
  ...columns,
];
